import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { FeaturesService } from '@core/services/features.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { AssetRouteName } from '@pages/assets-page/assets-page.module';
import { DamageReportsRouteName } from '@pages/damage-reports-page/damage-reports.routes';
import { EmissionsPageRouteName } from '@pages/emissions-page/emissions.routes';
import { FleetRouteName } from '@pages/fleet/fleet.routes';
import { POIRouteName } from '@pages/poi-page/poi-page.module';
import { ReportsRouteName } from '@pages/reports-page/reports-page.module';
import { OnCallRouteName } from '@pages/settings-page/settings-route-names';
import { SettingsRouteName } from '@pages/settings-page/settings.routes';
import { StatisticsRouteName } from '@pages/statistics-page/statistics.module';
import { UserRouteName } from '@pages/users-page/users-page.module';
import { VehicleRouteName } from '@pages/vehicles-page/vehicles-page.module';
import { Feature } from '@shared/constants/feature';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { combineLatest, map, Observable } from 'rxjs';

export const tripsGuard = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const permissionsService = inject(UserRolesAndPermissionsService);
  const featuresService = inject(FeaturesService);
  return combineLatest([
    permissionsService.getUserRolesAndPermissions(),
    featuresService.getFeatures(),
  ]).pipe(
    map(([{ Permissions }, features]) => {
      if (
        Permissions.includes(RolesAndPermissions.TripRead) &&
        features.includes(Feature.Trips)
      ) {
        return true;
      } else if (
        Permissions.includes(RolesAndPermissions.StatisticsRead) &&
        features.includes(Feature.Statistics)
      ) {
        return router.parseUrl('/' + StatisticsRouteName.Main);
      } else if (
        Permissions.includes(RolesAndPermissions.AssetRead) &&
        features.includes(Feature.Assets)
      ) {
        return router.parseUrl('/' + AssetRouteName.Main);
      } else if (
        Permissions.includes(RolesAndPermissions.VehicleRead) &&
        features.includes(Feature.Vehicles)
      ) {
        return router.parseUrl('/' + VehicleRouteName.Main);
      } else if (
        Permissions.includes(RolesAndPermissions.UserRead) &&
        features.includes(Feature.Users)
      ) {
        return router.parseUrl('/' + UserRouteName.Main);
      } else if (
        Permissions.includes(RolesAndPermissions.ReportRead) &&
        features.includes(Feature.Reports)
      ) {
        return router.parseUrl('/' + ReportsRouteName.Main);
      } else if (
        Permissions.includes(RolesAndPermissions.POIRead) &&
        features.includes(Feature.POI)
      ) {
        return router.parseUrl('/' + POIRouteName.Main);
      } else if (
        Permissions.includes(RolesAndPermissions.EmissionRead) &&
        features.includes(Feature.Emissions)
      ) {
        return router.parseUrl('/' + EmissionsPageRouteName.Main);
      } else if (
        features.includes(Feature.Fleet) &&
        Permissions.includes(RolesAndPermissions.FleetRead)
      ) {
        return router.parseUrl('/' + FleetRouteName.Main);
      } else if (
        Permissions.includes(RolesAndPermissions.StandbyRead) &&
        features.includes(Feature.Standby)
      ) {
        return router.parseUrl('/' + OnCallRouteName.Main);
      } else if (Permissions.includes(RolesAndPermissions.SettingsRead)) {
        return router.parseUrl('/' + SettingsRouteName.Main);
      } else if (features.includes(Feature.DamageReport)) {
        return router.parseUrl('/' + DamageReportsRouteName.Main);
      } else {
        return router.parseUrl('/no-rights');
      }
    })
  );
};
